/* verqo — Seller Landing (preview build)
   Compact seller-side landing that shows off the new mega-menu nav
   and gives quick access to every product/solutions/company page.
   Not the final production landing — a preview scaffold for review.
*/
import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2, CircleDot } from "lucide-react";
import { SubPageLayout, Reveal, Eyebrow, CTABanner } from "../../../components/marketing/SubPageBlocks";
import { SELLER_NAV } from "../../../config/nav.seller";

export default function SellerLandingPreview() {
  return (
    <SubPageLayout modeOverride="seller">
      {/* Hero */}
      <section className="relative pt-24 md:pt-32 pb-16 md:pb-20 px-6 md:px-10 max-w-[1280px] mx-auto" data-testid="seller-landing-hero">
        <div className="grid grid-cols-1 lg:grid-cols-[1.15fr_1fr] gap-12 lg:gap-16 items-center">
          <div>
            <Reveal>
              <Eyebrow>Sellers · Brands, distributors, 3PLs</Eyebrow>
            </Reveal>
            <Reveal delay={0.08}>
              <h1 className="mt-6 text-[40px] sm:text-[52px] md:text-[76px] leading-[1.0] tracking-tight font-semibold text-slate-900">
                Wholesale,{" "}
                <span className="bg-lime-300 px-2.5 -rotate-1 inline-block my-1">instant.</span>
              </h1>
            </Reveal>
            <Reveal delay={0.18}>
              <p className="mt-6 max-w-xl text-[16px] md:text-[18px] leading-relaxed text-slate-600">
                One platform for the entire wholesale channel — catalog, terms, payments, shipping, insurance. Get paid on day 1. Ship your first invoice in 48 hours.
              </p>
            </Reveal>
            <Reveal delay={0.28}>
              <div className="mt-9 flex flex-wrap gap-3 items-center">
                <a href="/waitlist?as=seller" className="group inline-flex items-center gap-2 px-6 py-3.5 rounded-lg text-white text-sm font-semibold hover:opacity-90 transition-opacity bg-[#0c1612]">
                  Get started <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </a>
                <a href="/product/catalog" className="inline-flex items-center gap-2 px-5 py-3 rounded-lg border border-slate-300 text-slate-900 text-sm hover:bg-slate-50 transition-colors">
                  Tour the product
                </a>
              </div>
            </Reveal>
            <Reveal delay={0.36}>
              <div className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-2 text-[12px] text-slate-500">
                <div className="inline-flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-lime-600" /> Same-day cash on every invoice</div>
                <div className="inline-flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-lime-600" /> Allianz-backed terms</div>
              </div>
            </Reveal>
          </div>
          <Reveal delay={0.15} y={32}>
            <div className="rounded-2xl p-8 md:p-10 overflow-hidden shadow-[0_40px_80px_-32px_rgba(15,23,42,0.4)] bg-[#0c1612]">
              <div className="text-[11px] uppercase tracking-[0.28em] text-lime-300 mb-6">Explore the product</div>
              {SELLER_NAV.filter(t => t.mega).map((tab) => (
                <div key={tab.id} className="mb-6 last:mb-0">
                  <div className="text-[10.5px] uppercase tracking-[0.22em] text-white/50 font-semibold mb-2">{tab.label}</div>
                  <div className="flex flex-wrap gap-1.5">
                    {tab.columns.flatMap(c => c.items).slice(0, 8).map(it => (
                      <Link
                        key={it.href}
                        to={it.href}
                        className="text-[11.5px] px-2.5 py-1 rounded-full bg-white/10 hover:bg-lime-300 hover:text-slate-900 text-white/85 transition-colors"
                        data-testid={`seller-landing-chip-${it.slug}`}
                      >
                        {it.label}
                      </Link>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* Sitemap quick browse */}
      <section className="relative py-16 md:py-24 px-6 md:px-10 max-w-[1280px] mx-auto" data-testid="seller-landing-sitemap">
        <div className="text-[10.5px] uppercase tracking-[0.28em] text-slate-500 font-semibold mb-6">
          Every page, one click away
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {SELLER_NAV.filter(t => t.mega).flatMap(t => t.columns).map((col) => (
            <div key={col.heading} className="rounded-2xl border border-slate-200 bg-white p-6">
              <div className="text-[10.5px] uppercase tracking-[0.22em] text-slate-500 font-semibold mb-4">{col.heading}</div>
              <ul className="space-y-2.5">
                {col.items.map((it) => (
                  <li key={it.href}>
                    <Link
                      to={it.href}
                      className="group inline-flex items-center gap-1.5 text-[13.5px] text-slate-800 hover:text-lime-800 transition-colors"
                    >
                      {it.label}
                      <ArrowRight className="w-3 h-3 opacity-0 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all" />
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>

      <CTABanner
        title="Ship your first invoice in 48 hours."
        subtitle="No implementation fees. No long contracts. Just wholesale, instant."
        primary={{ label: "Get started", href: "/waitlist?as=seller" }}
        secondary={{ label: "Talk to sales", href: "/company/contact" }}
      />
    </SubPageLayout>
  );
}
